import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { ReactComponent as ReturnIcon } from '../../assets/icons/rotate-right.svg';
import SnackbarComponent from '../Snackbar/SnackbarComponent';
import GritButton from '../ui/GritButton';
import {
  resellerLogoutOfClient,
  superUserLogoutOfClient,
} from '../../services/Api/userService';
import {
  COLORS,
  FONTS,
  MOTION,
  RADII,
  REDUCED_MOTION,
} from '../../theme/tokens';
import { authCookieOptions } from '../../utils/cookieOptions';
import { adoptSessionToken } from '../../utils/sessionRefresh';
import { useSnackbar } from '../../utils/useSnackbar';

const ClientAccessIndicator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cookies, setCookie] = useCookies([
    'user',
    'original_company_id',
    'super_user',
  ]);
  const snackbar = useSnackbar();

  const isSuperUser = cookies.super_user;
  const isReseller = cookies.original_company_id && !isSuperUser;
  const isOnResellerPage = location.pathname.startsWith('/reseller');

  const handleReturnClick = async () => {
    try {
      // If super user, call the logout endpoint
      if (isSuperUser) {
        const response = await superUserLogoutOfClient({
          email: cookies.user.email,
          company_id: cookies.user.company_id,
        });

        if (response.statusCode === 200) {
          // Swap back to the unscoped super-user token; keeping the
          // client-scoped one would leak client scope into the admin console.
          if (response.data?.token) {
            adoptSessionToken(response.data.token);
          }
          setCookie(
            'user',
            { ...cookies.user, company_id: '' },
            authCookieOptions()
          );
          // Navigate to admin resellers page if currently on reseller page, otherwise clients page
          navigate(isOnResellerPage ? '/admin/resellers' : '/admin/clients');
        } else {
          snackbar.showError('Failed to return to admin console');
        }
      } else if (isReseller) {
        // Re-mint a token acting as the original reseller company — the
        // current one is scoped to the client and would be rejected on
        // every reseller-portal endpoint.
        const response = await resellerLogoutOfClient({
          reseller_company_id: cookies.original_company_id,
        });

        if (response.statusCode === 200) {
          if (response.data?.token) {
            adoptSessionToken(response.data.token);
          }
          setCookie(
            'user',
            { ...cookies.user, company_id: cookies.original_company_id },
            authCookieOptions()
          );
          setCookie('original_company_id', '', authCookieOptions({ maxAge: 0 })); // Clear the cookie
          navigate('/reseller/clients');
        } else {
          snackbar.showError('Failed to return to console');
        }
      }
    } catch {
      snackbar.showError('Failed to return to console');
    }
  };

  const buttonText = 'Return to Admin Console';

  return (
    <>
      <GritButton
        intent="secondary"
        trailingIcon={<ReturnIcon />}
        sx={{
          'position': 'fixed',
          'top': 25,
          'left': '50%',
          // ponytail: size to the label. A fixed width clipped the text at
          // zoom / narrow viewports; maxWidth keeps it inside small screens.
          'minHeight': '34px',
          'height': 'auto',
          'px': 2,
          'maxWidth': 'calc(100vw - 32px)',
          'whiteSpace': 'nowrap',
          'transform': 'translateX(-50%)',
          'backgroundColor': COLORS.frameNavy,
          'color': COLORS.onFrame,
          'boxShadow': 'none',
          'textTransform': 'none',
          'fontWeight': 600,
          'fontFamily': FONTS.body,
          'borderRadius': `${RADII.pill}px`,
          'fontSize': '15px',
          'lineHeight': '28px',
          'zIndex': 1500,
          'transition': `background-color ${MOTION.durationShort}ms ${MOTION.easeOut}, transform ${MOTION.durationShort}ms ${MOTION.easeOut}`,
          '&:hover': {
            backgroundColor: COLORS.frameNavyTint,
            transform: 'translateX(-50%) translateY(-1px)',
          },
          '&.Mui-disabled': {
            backgroundColor: COLORS.disabledBg,
            color: COLORS.borderStrong,
          },
          [REDUCED_MOTION]: {
            'transition': 'none',
            '&:hover': {
              transform: 'translateX(-50%)',
            },
          },
        }}
        onClick={handleReturnClick}
      >
        {buttonText}
      </GritButton>
      <SnackbarComponent
        message={snackbar.message}
        severity={snackbar.severity}
        open={snackbar.open}
        onClose={snackbar.close}
        onRetry={snackbar.retry}
      />
    </>
  );
};
export default ClientAccessIndicator;
